import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Post } from 'src/app/doggogram/post.model';
import { CommentsModalComponent } from '../comments-modal/comments-modal.component';
import {
  ActionSheetController,
  AlertController,
  LoadingController,
  ModalController,
  ToastController,
} from '@ionic/angular';
import { ToggleLikeDTO } from 'src/app/doggogram/doggogram.dto';
import { DoggogramService } from 'src/app/doggogram/doggogram.service';
import { Subscription } from 'rxjs';
import { formatDate } from '@angular/common';
import { Router } from '@angular/router';
import { EditCaptionModalComponent } from './edit-caption-modal/edit-caption-modal.component';
import { environment } from 'src/environments/environment';
import { Share } from '@capacitor/share';
import { Clipboard } from '@capacitor/clipboard';
import { AuthService } from 'src/app/auth/auth.service';
import { ReportService } from '../services/report.service';
import { ChatService } from 'src/app/chats/chats.service';

@Component({
  selector: 'app-post-card',
  templateUrl: './post-card.component.html',
  styleUrls: ['./post-card.component.scss'],
})
export class PostCardComponent implements OnInit, OnDestroy {
  likeSub: Subscription;
  @Input('post') post: Post;
  @Input('posts') posts: Post[];
  @Input('userId') userId: number;
  hideDelete = true;
  lastClickTime = 0;
  doubleClickThreshold = 300;
  isLikeDisabled = false;
  isAdmin = false;
  constructor(
    private modalCtrl: ModalController,
    private toastController: ToastController,
    private doggogramService: DoggogramService,
    private router: Router,
    private alertCtrl: AlertController,
    private loadingCtrl: LoadingController,
    private actionSheetCtrl: ActionSheetController,
    private authService: AuthService,
    private reportService: ReportService,
    private chatService: ChatService
  ) { }
  async ngOnInit() {
    this.isAdmin = await this.authService.isAdmin();
    this.hideDelete = this.router.url.toLowerCase().includes('doggogram');
  }
  formatDate(myDate: Date) {
    const format = 'MMMM dd yyyy';
    const locale = 'en-US';
    const formattedDate = formatDate(myDate, format, locale);
    return formattedDate;
  }
  ngOnDestroy() {
    if (this.likeSub) {
      this.likeSub.unsubscribe();
    }
  }
  async ViewComments(postid: number, postUserId: number) {
    const userId = await this.authService.getUserId();
    if (userId <= 0) {
      this.ShowLoginAlert();
      return;
    }
    this.modalCtrl
      .create({
        component: CommentsModalComponent,
        componentProps: {
          postid,
          postUserId,
          userId: userId,
          source: 'doggogram',
        },
      })
      .then((modalEl) => {
        modalEl.onDidDismiss().then((modalData) => {
          if (!modalData.data) {
            return;
          }
          const index = this.posts.findIndex((p) => p.id === postid);
          this.posts[index].commentsCount = modalData.data['commentsCount'];
        });
        modalEl.present();
      });
  }

  CheckDoubleClick(postid: number) {
    const currentTime = new Date().getTime();
    if (currentTime - this.lastClickTime < this.doubleClickThreshold) {
      this.ToggleLike(postid);
    }
    this.lastClickTime = currentTime;
  }
  async ToggleLike(postid: number) {
    const userId = await this.authService.getUserId();
    if (this.isLikeDisabled == true) return;
    if (userId <= 0) {
      this.ShowLoginAlert();
      return;
    }
    this.isLikeDisabled = true;
    const index = this.posts.findIndex((p) => p.id === postid);
    this.posts[index].isLiked = !this.posts[index].isLiked;
    if (this.posts[index].isLiked)
      this.posts[index].likesCount = this.posts[index].likesCount + 1;
    else this.posts[index].likesCount = this.posts[index].likesCount - 1;
    const toggleLikeDTO: ToggleLikeDTO = { postid, userid: userId };
    this.likeSub = this.doggogramService.toggleLike(toggleLikeDTO).subscribe({
      next: (res) => {
        this.posts[index].likesCount = res;
        this.isLikeDisabled = false;
      },
      error: async () => {
        this.isLikeDisabled = false;
        const toast = await this.toastController.create({
          color: 'danger',
          duration: 2000,
          message: 'Could not perform action. Please try again later.',
        });
        await toast.present();
      },
    });
  }

  ShowLoginAlert() {
    this.alertCtrl
      .create({
        header: 'Sign In Reqiured',
        message: 'You need to sign in first to view or add comments or likes',
        buttons: [
          {
            text: 'Dismiss',
            handler: () => {
              this.alertCtrl.dismiss();
            },
          },
          {
            text: 'Sign in',
            handler: () => {
              this.router.navigateByUrl('/signin?redirectURL=doggogram');
            },
          },
        ],
      })
      .then((alertEl) => alertEl.present());
  }
  async presentActionSheet(post) {
    const userId = await this.authService.getUserId();
    const isUserAdded = post.userid === userId || this.isAdmin;
    const buttons = [
      {
        text: 'Copy Link',
        icon: 'copy-outline',
        handler: () => {
          this.copyLink(post.id);
        },
      },
      {
        text: 'Share Post',
        icon: 'share-social-outline',
        handler: () => {
          this.sharePost(post.id);
        },
      },
      // Conditionally add the Delete button
      ...(isUserAdded
        ? [
          {
            text: 'Edit Caption',
            icon: 'pencil-outline',
            handler: () => {
              this.editCaption(post.id);
            },
          },
          {
            text: 'Delete',
            icon: 'trash-outline',
            role: 'destructive',
            handler: () => {
              this.deletePost(post.id, post.imageUrl);
            },
            cssClass: 'danger-button',
          },
        ]
        : []), // Empty if condition is false
      ...(!isUserAdded
        ? [
          {
            text: 'Report Post',
            icon: 'nuclear-outline',
            role: 'destructive',
            handler: () => {
              this.reportPost(post.id);
            },
            cssClass: 'danger-button',
          },
        ]
        : []),
    ];

    const actionSheet = await this.actionSheetCtrl.create({
      buttons: buttons,
    });

    await actionSheet.present();
  }

  deletePost(postId: number, imageURL: string) {
    this.alertCtrl
      .create({
        header: 'Delete Post?',
        message: 'Are you sure you want to delete this post?',
        buttons: [
          {
            text: 'Cancel',
            handler: () => {
              this.alertCtrl.dismiss();
            },
          },
          {
            text: 'Delete',
            cssClass: 'confirm-delete',
            handler: () => {
              this.performDelete(postId, imageURL);
              this.alertCtrl.dismiss();
            },
          },
        ],
      })
      .then((alertEl) => alertEl.present());
  }

  performDelete(postId: number, downloadUrl: string) {
    this.loadingCtrl
      .create({
        keyboardClose: true,
        message: 'Please wait...',
      })
      .then((loadingEl) => {
        loadingEl.present();
        this.doggogramService.deletePost(postId, downloadUrl).subscribe({
          next: async (res) => {
            if (res) {
              const index = this.posts.findIndex((p) => p.id === postId);
              this.posts.splice(index, 1);
              loadingEl.dismiss();
              const toast = await this.toastController.create({
                color: 'primary',
                duration: 2000,
                message: 'Post deleted successfully.',
              });
              await toast.present();
            } else {
              loadingEl.dismiss();
              const toast = await this.toastController.create({
                color: 'danger',
                duration: 2000,
                message: 'Could not perform action. Please try again later.',
              });
              await toast.present();
            }
          },
          error: async () => {
            loadingEl.dismiss();
            const toast = await this.toastController.create({
              color: 'danger',
              duration: 2000,
              message: 'Could not perform action. Please try again later.',
            });
            await toast.present();
          },
        });
      });
  }

  reportPost(postId: number) {
    this.reportService.report('post', postId.toString(), this.userId)
  }

  editCaption(postId: number) {
    const index = this.posts.findIndex((p) => p.id === postId);
    const caption = this.posts[index].caption;
    const imageUrl = this.posts[index].imageUrl;
    this.modalCtrl
      .create({
        component: EditCaptionModalComponent,
        componentProps: {
          caption,
          imageUrl,
        },
      })
      .then((modalEl) => {
        modalEl.onDidDismiss().then((modalData) => {
          if (!modalData.data) {
            return;
          }
          this.loadingCtrl
            .create({
              keyboardClose: true,
              message: 'Please wait...',
            })
            .then((loadingEl) => {
              loadingEl.present();
              const caption = modalData.data['caption'];
              this.doggogramService.updateComment(postId, caption).subscribe({
                next: async (res) => {
                  if (res) {
                    const index = this.posts.findIndex((p) => p.id === postId);
                    this.posts[index].caption = caption;
                    loadingEl.dismiss();
                    const toast = await this.toastController.create({
                      color: 'primary',
                      duration: 2000,
                      message: 'Caption updated successfully.',
                    });
                    await toast.present();
                  } else {
                    loadingEl.dismiss();
                    const toast = await this.toastController.create({
                      color: 'danger',
                      duration: 2000,
                      message:
                        'Could not perform action. Please try again later.',
                    });
                    await toast.present();
                  }
                },
                error: async () => {
                  loadingEl.dismiss();
                  const toast = await this.toastController.create({
                    color: 'danger',
                    duration: 2000,
                    message:
                      'Could not perform action. Please try again later.',
                  });
                  await toast.present();
                },
              });
            });
        });
        modalEl.present();
      });
  }

  async sharePost(postId: number) {
    const url = environment.appUrl + '/doggogram/post/' + postId;
    await Share.share({
      title: 'Cutie Alert!',
      text: `Check out this cutie!! ${url}`, // Combine text and URL
    });
  }

  async copyLink(postId: number) {
    const url = environment.appUrl + '/doggogram/post/' + postId;
    try {
      await Clipboard.write({
        string: url,
      });

      const toast = await this.toastController.create({
        message: 'URL copied to clipboard!',
        color: 'primary',
        duration: 2000,
        position: 'bottom',
      });
      toast.present();
    } catch (error) {
      console.error('Failed to copy URL:', error);
      const toast = await this.toastController.create({
        message: 'Failed to copy URL.',
        duration: 2000,
        position: 'bottom',
      });
      toast.present();
    }
  }

  openUserOptions(userId: number, username: string) {
    this.chatService.openUserOptions(userId.toString(), username);
  }
}
